"use client"

import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react"
import { Check } from "lucide-react"

import { ChipPopover } from "@/components/graphite/chip-popover"
import { cn } from "@/lib/utils"

/**
 * One choice from a short list, as a chip that opens the list.
 *
 * This replaces the pattern it is named after: a styled box with an
 * `opacity-0` native `<select>` stretched over its face. That arrangement puts
 * the actual control in the browser's hands — the menu opens where the platform
 * decides, wearing the platform's styling, at the platform's size — while the
 * box underneath is a picture of a control that cannot show which option is
 * selected, cannot be themed, and gives no sign it is even clickable.
 *
 * So the list is drawn here: a real listbox under the chip, the current option
 * marked, arrows moving through it, and the same dismissal rules every other
 * panel in the app follows.
 */

export type SelectOption<T extends string> = { value: T; label: string }

/**
 * The list itself, mounted with the panel.
 *
 * Its own component so that where the keyboard is standing begins at the
 * current choice every time the list appears, and is forgotten when it goes —
 * held in the chip, it would need resetting by hand on open, and the reset that
 * got forgotten is the one that reopens the list halfway down.
 */
function OptionList<T extends string>({
  options,
  value,
  listLabel,
  onPick,
}: {
  options: readonly SelectOption<T>[]
  value: T
  listLabel: string
  onPick: (next: T) => void
}) {
  const list = useRef<HTMLDivElement>(null)
  /**
   * The one option that is a tab stop. A list of twenty companies is one stop
   * with arrows inside it, not twenty between this filter and the next.
   */
  const [at, setAt] = useState(() =>
    Math.max(
      0,
      options.findIndex((option) => option.value === value),
    ),
  )

  useEffect(() => {
    list.current?.querySelector<HTMLElement>(`[data-index="${at}"]`)?.focus()
  }, [at])

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const last = options.length - 1
    const next =
      event.key === "ArrowDown"
        ? Math.min(last, at + 1)
        : event.key === "ArrowUp"
          ? Math.max(0, at - 1)
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? last
              : undefined
    if (next === undefined) return
    event.preventDefault()
    setAt(next)
  }

  return (
    // The list carries the role, so the options are its own children.
    <div ref={list} role="listbox" aria-label={listLabel} onKeyDown={onKeyDown}>
      {options.map((option, index) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="option"
            aria-selected={selected}
            data-index={index}
            tabIndex={index === at ? 0 : -1}
            onClick={() => onPick(option.value)}
            className={cn(
              "flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[12.5px] leading-[1.4] transition-colors",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary",
              selected ? "text-brand-ink" : "text-foreground hover:bg-foreground/[0.06]",
            )}
          >
            <span className="min-w-0 flex-1 truncate">{option.label}</span>
            {/* The tick keeps its space either way, so the labels line up. */}
            <Check
              aria-hidden
              className={cn("size-3.5 flex-none", selected ? "" : "invisible")}
            />
          </button>
        )
      })}
    </div>
  )
}

export function SelectChip<T extends string>({
  label,
  options,
  value,
  onChange,
  listLabel,
  active,
  mono,
  className,
  triggerClassName,
}: {
  /** The quiet prefix — "Fee". Omitted where the value names itself. */
  label?: string
  options: readonly SelectOption<T>[]
  value: T
  onChange: (next: T) => void
  /** Names the list for a screen reader — "Filter by company". */
  listLabel: string
  /**
   * Whether the filter is doing something. Stated rather than inferred: only
   * the caller knows whether its first option means "everything".
   */
  active?: boolean
  mono?: boolean
  className?: string
  triggerClassName?: string
}) {
  const current = options.find((option) => option.value === value)

  return (
    <ChipPopover
      label={label}
      // A chip that shows only its value — "All companies" — says nothing about
      // what it does, so the list's name stands in for the missing prefix.
      purpose={label ? undefined : listLabel}
      value={current?.label ?? value}
      active={active}
      mono={mono}
      haspopup="listbox"
      panelClassName="max-h-[280px] overflow-y-auto p-1"
      className={className}
      triggerClassName={triggerClassName}
    >
      {({ close }) => (
        <OptionList
          options={options}
          value={value}
          listLabel={listLabel}
          onPick={(next) => {
            onChange(next)
            close()
          }}
        />
      )}
    </ChipPopover>
  )
}
